// src/services/milestoneWall.js
//
// ── The Milestone Wall ───────────────────────────────────────────────────
//
// The shared feed where members' milestones live so everyone can cheer each
// other on. Three collections, all NEW (each needs a Firestore console rule —
// without one, writes silently deny; see reference_firestore_rules):
//
//   milestones         `${userId}_${badgeKey}`      one doc per earned badge
//   milestoneCheers    `${userId}_${milestoneId}`   one cheer per member per milestone
//   milestoneComments  auto                          soft-deleted, like classComments
//
// Design decisions (locked 2026-08-24):
//   • Seeded with REAL earned dates, not "just now". The wall is alive from the
//     first visit and history sorts naturally instead of flooding the top.
//   • A former member's milestones STAY. A milestone was a real moment when it
//     happened; deleting it rewrites history and shrinks the wall over time.
//   • Names are "Ashley B" — matching the live presence chips members already
//     see. No opt-out.
//
// Why the member's own client publishes their milestones: earned dates are
// derived from their completions, which only their session has loaded. A server
// job would have to re-read every member's full completion history to learn the
// same thing.

import {
    collection, doc, getDoc, getDocs, query, where, orderBy, limit, startAfter, increment, serverTimestamp,
} from 'firebase/firestore';
// Writes wait for the member's Firestore identity (adr-firestore-identity.md a2).
import {
    setDoc, addDoc, updateDoc, deleteDoc,
} from './firestoreWrite';
import { db, firebaseEnabled } from './firebase';

export const WALL_PAGE_SIZE = 10;

// "Ashley Bennett" → "Ashley B". Matches ViewerPresenceRow's convention.
// Returns NULL when there is no name on file — callers must decide what that
// means. For Wall entries it means "don't publish": "Someone earned 25 Class
// Club" reads as a bug rather than as anonymity.
export function displayNameFor(user) {
    if (!user) return null;
    const first = (user.firstName || '').trim();
    const last = (user.lastName || '').trim();
    if (!first && !last) return null;
    if (!last) return first;
    return `${first} ${last.charAt(0).toUpperCase()}`;
}

export function milestoneIdFor(userId, badgeKey) {
    return `${userId}_${badgeKey}`;
}

// ── Publishing ───────────────────────────────────────────────────────────
//
// Idempotent by construction: the doc id is deterministic, so a repeat publish
// overwrites with identical values rather than creating duplicates.
//
// `alreadyPublished` (a localStorage-backed set, owned by the caller) keeps the
// common case at ZERO writes — without it every Home load would re-write every
// earned badge. The deterministic id is the correctness guarantee; the local
// set is purely a cost guard.
export async function publishMilestones(userId, displayName, earned, alreadyPublished = new Set()) {
    if (!firebaseEnabled || !userId || !Array.isArray(earned)) return [];
    // No name ⇒ no Wall entry. Skipped rather than published as a placeholder.
    if (!displayName) return [];

    const toPublish = earned.filter((b) => b.key && !alreadyPublished.has(b.key));
    if (toPublish.length === 0) return [];

    const published = [];
    await Promise.all(
        toPublish.map(async (b) => {
            try {
                const id = milestoneIdFor(userId, b.key);
                // earnedAt is the TRUE moment — the completedAt of the Nth class,
                // or the end of the Nth consecutive week. Falling back to the CT
                // day keeps consistency badges (which have no instant) sortable.
                const earnedAt = b.earnedAt || (b.earnedDayCT ? `${b.earnedDayCT}T12:00:00.000Z` : new Date().toISOString());
                await setDoc(
                    doc(db, 'milestones', id),
                    {
                        userId,
                        displayName,
                        badgeKey: b.key,
                        badgeName: b.name || '',
                        earnedAt,
                        earnedDayCT: b.earnedDayCT || null,
                        publishedAt: serverTimestamp(),
                    },
                    // merge: never clobber cheerCount / commentCount, which are
                    // owned by other members' interactions.
                    { merge: true },
                );
                published.push(b.key);
            } catch (e) {
                // Best-effort: a failed publish must never break Home.
                console.error('Error publishing milestone:', b.key, e);
            }
        }),
    );
    return published;
}

// ── Reading the wall ─────────────────────────────────────────────────────
// Newest-earned first, truly paginated: `cursor` is the last document from the
// previous page, so a member can keep walking back through the whole history to
// cheer on older milestones rather than hitting a ceiling.
// Single-field orderBy ⇒ automatic index, no composite.
export async function fetchWall(pageSize = WALL_PAGE_SIZE, cursor = null) {
    if (!firebaseEnabled) return { rows: [], lastDoc: null, hasMore: false };
    try {
        const base = [collection(db, 'milestones'), orderBy('earnedAt', 'desc')];
        const q = cursor
            ? query(...base, startAfter(cursor), limit(pageSize))
            : query(...base, limit(pageSize));
        const snap = await getDocs(q);
        return {
            rows: snap.docs.map((d) => ({ id: d.id, ...d.data() })),
            lastDoc: snap.docs[snap.docs.length - 1] || null,
            // A short page means the end; a full page means there may be more.
            hasMore: snap.docs.length === pageSize,
        };
    } catch (e) {
        console.error('Error fetching milestone wall:', e);
        return { rows: [], lastDoc: null, hasMore: false };
    }
}

// ── Cheers ───────────────────────────────────────────────────────────────
// One per member per milestone, enforced by a composite doc id. The count is
// denormalised onto the milestone doc so rendering the wall never needs a
// per-row query.
export function cheerIdFor(userId, milestoneId) {
    return `${userId}_${milestoneId}`;
}

export async function fetchMyCheers(userId, milestoneIds = []) {
    if (!firebaseEnabled || !userId || milestoneIds.length === 0) return new Set();
    try {
        const results = await Promise.all(
            milestoneIds.map(async (mid) => {
                const snap = await getDoc(doc(db, 'milestoneCheers', cheerIdFor(userId, mid)));
                return snap.exists() ? mid : null;
            }),
        );
        return new Set(results.filter(Boolean));
    } catch (e) {
        console.error('Error loading cheers:', e);
        return new Set();
    }
}

export async function toggleCheer(userId, milestoneId, on) {
    if (!firebaseEnabled || !userId || !milestoneId) return false;
    const ref = doc(db, 'milestoneCheers', cheerIdFor(userId, milestoneId));
    try {
        if (on) {
            await setDoc(ref, { userId, milestoneId, createdAt: serverTimestamp() });
        } else {
            await deleteDoc(ref);
        }
        // Denormalised counter. Best-effort — the cheer doc is the source of truth.
        updateDoc(doc(db, 'milestones', milestoneId), {
            cheerCount: increment(on ? 1 : -1),
        }).catch((e) => console.error('Error updating cheerCount:', e));
        return true;
    } catch (e) {
        console.error('Error toggling cheer:', e);
        return false;
    }
}

// ── Comments ─────────────────────────────────────────────────────────────
//
// A SEPARATE collection from classComments on purpose. classComments carries
// the reply-threading, backfill and edge-of-window preservation logic behind
// COMMENTS-001/002/003; adding a second target type to it would put that
// regression-sensitive code in the blast radius of a brand-new social surface.
// This is a simpler, flat thread.
export async function fetchMilestoneComments(milestoneId) {
    if (!firebaseEnabled || !milestoneId) return [];
    try {
        const snap = await getDocs(
            query(
                collection(db, 'milestoneComments'),
                where('milestoneId', '==', milestoneId),
                where('status', '==', 'active'),
            ),
        );
        // Sorted client-side: a where+where+orderBy would need a composite index,
        // and a single milestone's thread is small.
        //
        // This deliberately fetches the WHOLE thread and lets the UI show only the
        // most recent few. At realistic volumes (tens of comments on a milestone)
        // one small query beats paging, and it avoids a composite index for a
        // problem that doesn't exist yet. If a thread ever reaches the hundreds,
        // switch to orderBy('createdAtIso','desc') + limit and add the index.
        return snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => String(a.createdAtIso || '').localeCompare(String(b.createdAtIso || '')));
    } catch (e) {
        console.error('Error fetching milestone comments:', e);
        return [];
    }
}

export async function postMilestoneComment(milestoneId, userId, displayName, text, isAdmin = false) {
    if (!firebaseEnabled || !milestoneId || !userId) return null;
    const clean = String(text || '').trim().slice(0, 280);
    if (!clean) return null;
    try {
        const createdAtIso = new Date().toISOString();
        const data = {
            milestoneId,
            userId,
            displayName: displayName || 'Someone',
            text: clean,
            isCoach: !!isAdmin,
            status: 'active',
            createdAtIso,
            createdAt: serverTimestamp(),
        };
        const ref = await addDoc(collection(db, 'milestoneComments'), data);
        updateDoc(doc(db, 'milestones', milestoneId), {
            commentCount: increment(1),
        }).catch((e) => console.error('Error updating commentCount:', e));
        return { id: ref.id, ...data };
    } catch (e) {
        console.error('Error posting milestone comment:', e);
        return null;
    }
}

// Soft delete, mirroring classComments. Author or admin only — enforced at the
// app layer, consistent with the app's existing posture (see Known Gaps).
export async function deleteMilestoneComment(commentId, milestoneId, userId, isAdmin = false) {
    if (!firebaseEnabled || !commentId) return false;
    try {
        const ref = doc(db, 'milestoneComments', commentId);
        const snap = await getDoc(ref);
        if (!snap.exists()) return false;
        if (!isAdmin && snap.data().userId !== userId) return false;
        await updateDoc(ref, { status: 'deleted', deletedAt: serverTimestamp() });
        if (milestoneId) {
            updateDoc(doc(db, 'milestones', milestoneId), {
                commentCount: increment(-1),
            }).catch((e) => console.error('Error updating commentCount:', e));
        }
        return true;
    } catch (e) {
        console.error('Error deleting milestone comment:', e);
        return false;
    }
}

// ── Publish bookkeeping (localStorage cost guard) ────────────────────────
const LS_KEY = 'mom_milestones_published_v1';

export function readPublished(userId) {
    if (!userId || typeof window === 'undefined') return new Set();
    try {
        const raw = window.localStorage.getItem(`${LS_KEY}:${userId}`);
        return new Set(raw ? JSON.parse(raw) : []);
    } catch (_) { return new Set(); }
}

export function writePublished(userId, keys) {
    if (!userId || typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(`${LS_KEY}:${userId}`, JSON.stringify(Array.from(keys)));
    } catch (_) { /* private mode / quota — deterministic ids keep this safe */ }
}
